import { useCallback, useMemo, useRef, type MutableRefObject, type RefObject } from 'react';

import {
    AXIS_PREVIEW_SALES_KPI_IDS,
    getAxisLabel,
} from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import {
    collectConfiguredPackArchitectureKpiIds,
    createPackArchitectureKpiIdMatcher,
} from 'components/pages/PackArchitecture/packArchitectureKpiIdMatching.helpers';
import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PPA_PACK_ARCH_BUBBLE_OR_CELL_SELECTED } from 'utils/mixpanelEventNames';

import {
    buildTableData,
    findCellKpi,
    getPackArchitectureTableEmptyDescription,
    getPackArchitectureTableFooterLabel,
    getTableCellHeightForKpiCount,
    TABLE_EMPTY_STATE_MIN_HEIGHT,
    TABLE_SCROLL_Y,
} from './packArchitectureTable.helpers';
import { usePackArchitectureTableScroll } from './usePackArchitectureTableScroll';
import { usePackArchitectureTableCellHover } from './usePackArchitectureTableCellHover';
import { usePackArchitectureTableMeta } from './usePackArchitectureTableMeta';
import { usePackArchitectureTableRowInteractions } from './usePackArchitectureTableRowInteractions';

type UsePackArchitectureTableParams = {
    tableWrapperRef?: RefObject<HTMLDivElement | null>;
    isSyncingTableFromChartRef?: MutableRefObject<boolean>;
    suppressChartToTableSyncRef?: MutableRefObject<boolean>;
};

export function usePackArchitectureTable({
    tableWrapperRef: tableWrapperRefProp,
    isSyncingTableFromChartRef,
    suppressChartToTableSyncRef,
}: UsePackArchitectureTableParams = {}) {
    const {
        tableRows,
        effectiveRowCount,
        tableTooltip,
        xAxis,
        xAxisOptionGroups,
        yAxis,
        yAxisOptionGroups,
        bubbleRepresents,
        bubbleRepresentsOptionGroups,
        bubbleSizeOptionGroups,
        tableKpiOptionItems,
        selectedKpis,
        toggleTableKpi,
        tableSearchInput,
        setTableSearchInput,
        submitTableSearch,
        tableSearchKeyword,
        isChartLoading,
        isTablePaginatedLoading,
        isChartPaginatedLoading,
        isChartTableRefetching,
        isChartTableLoaderRefetching,
        isChartPanelLoading,
        chartError,
        tableHasMorePages,
        fetchNextTablePage,
        activeSegmentKey,
        selectedColumnKey,
        selectSegment,
        selectTableCell,
        clearSelection,
    } = usePackArchitectureContext();

    const { tableKpiOptions, tableTitle, kpiOptions } = usePackArchitectureTableMeta({
        xAxis,
        xAxisOptionGroups,
        yAxis,
        yAxisOptionGroups,
        bubbleRepresents,
        bubbleRepresentsOptionGroups,
        tableKpiOptionItems,
    });

    const [search, setSearch] = [tableSearchInput, setTableSearchInput];
    const internalTableWrapperRef = useRef<HTMLDivElement | null>(null);
    const tableWrapperRef = tableWrapperRefProp ?? internalTableWrapperRef;

    const orderedSelectedKpis = useMemo(() => {
        const order = [yAxis, ...tableKpiOptions.map((option) => option.id)];
        return order.filter((id) => id && selectedKpis.includes(id));
    }, [selectedKpis, yAxis, tableKpiOptions]);

    const kpiIdMatcher = useMemo(
        () =>
            createPackArchitectureKpiIdMatcher({
                configuredKpiIds: collectConfiguredPackArchitectureKpiIds(
                    yAxisOptionGroups,
                    tableKpiOptionItems,
                    AXIS_PREVIEW_SALES_KPI_IDS,
                    bubbleSizeOptionGroups,
                ),
                salesKpiIds: AXIS_PREVIEW_SALES_KPI_IDS,
            }),
        [bubbleSizeOptionGroups, tableKpiOptionItems, yAxisOptionGroups],
    );

    const { columns, tableRows: builtTableRows } = useMemo(() => buildTableData(tableRows, yAxis), [tableRows, yAxis]);
    const loadedColumnCount = columns.length;
    // Keep table pagination aligned with chart pagination by rendering all
    // columns returned so far. A hard cap here causes chart-only appends.
    const visibleColumns = useMemo(() => columns, [columns]);
    const columnIndexByKey = useMemo(() => {
        const indexByKey = new Map<string, number>();
        visibleColumns.forEach((column, index) => indexByKey.set(column.key, index));
        return indexByKey;
    }, [visibleColumns]);
    const rowIndexByKey = useMemo(() => {
        const indexByKey = new Map<string, number>();
        builtTableRows.forEach((row, index) => indexByKey.set(row.key, index));
        return indexByKey;
    }, [builtTableRows]);
    const kpiTickCount = Math.max(orderedSelectedKpis.length, 1);
    const tableRowHeightPx = useMemo(() => getTableCellHeightForKpiCount(kpiTickCount), [kpiTickCount]);

    usePackArchitectureTableScroll({
        tableWrapperRef,
        builtTableRowCount: builtTableRows.length,
        visibleColumnCount: visibleColumns.length,
        loadedColumnCount,
        tableHasMorePages,
        isChartLoading,
        isTablePaginatedLoading,
        isChartPaginatedLoading,
        fetchNextTablePage,
        activeSegmentKey,
        selectedColumnKey,
        columnIndexByKey,
        rowIndexByKey,
        rowHeightPx: tableRowHeightPx,
        isSyncingTableFromChartRef,
        suppressChartToTableSyncRef,
    });

    const {
        hoveredRowKey,
        hoveredColumnKey,
        handleCellHover,
        handleColumnHeaderHover,
        clearCellHover,
    } = usePackArchitectureTableCellHover();

    const { getRowClassName, handleRowClick, clearHighlight, handleSubmitSearch } =
        usePackArchitectureTableRowInteractions({
            activeSegmentKey,
            selectSegment,
            clearSelection,
            submitTableSearch,
        });

    const handleTableCellClick = useCallback(
        (segmentKey: string, columnKey: string) => {
            const row = builtTableRows.find((tableRow) => tableRow.key === segmentKey);
            const column = columns.find((tableColumn) => tableColumn.key === columnKey);
            const yAxisEntry = findCellKpi(row?.cells[columnKey] ?? {}, yAxis, kpiIdMatcher.matches);

            trackEventMixpanel(PPA_PACK_ARCH_BUBBLE_OR_CELL_SELECTED, {
                selection_source: 'cell',
                x_axis: getAxisLabel(xAxis, xAxisOptionGroups),
                y_axis: getAxisLabel(yAxis, yAxisOptionGroups),
                bubble_represents_value: row?.label ?? segmentKey,
                x_axis_value: column?.label ?? columnKey,
                y_axis_value: yAxisEntry?.formatted,
            });
            selectTableCell(segmentKey, columnKey);
        },
        [
            builtTableRows,
            columns,
            kpiIdMatcher.matches,
            selectTableCell,
            xAxis,
            xAxisOptionGroups,
            yAxis,
            yAxisOptionGroups,
        ],
    );

    const isAppendingPages = isTablePaginatedLoading && tableRows.length > 0;
    const showTableLoader = isChartTableLoaderRefetching || (isChartTableRefetching && loadedColumnCount === 0);

    const isFiltered = tableSearchKeyword.length > 0;
    const showTable =
        !isChartPanelLoading ||
        tableRows.length > 0 ||
        isChartTableRefetching ||
        tableSearchKeyword.length > 0;
    const isTableEmpty = loadedColumnCount === 0 && !showTableLoader && !isAppendingPages && !isChartTableRefetching;
    const emptyStateTitle = isFiltered ? 'No matching rows found' : 'No data found';
    const emptyStateDescription = getPackArchitectureTableEmptyDescription(chartError, isFiltered);
    const footerLabel = getPackArchitectureTableFooterLabel(
        showTableLoader,
        isAppendingPages,
        loadedColumnCount,
        effectiveRowCount,
    );

    return {
        tooltip: tableTooltip,
        tableTitle,
        kpiOptions,
        selectedKpis,
        search,
        setSearch,
        submitSearch: handleSubmitSearch,
        clearHighlight,
        toggleKpi: toggleTableKpi,
        tableWrapperRef,
        visibleColumns,
        orderedSelectedKpis,
        yAxisKpiId: yAxis,
        activeSegmentKey,
        selectedColumnKey,
        hoveredRowKey,
        hoveredColumnKey,
        handleCellHover,
        handleColumnHeaderHover,
        handleTableCellClick,
        matchesKpiId: kpiIdMatcher.matches,
        filteredRows: builtTableRows,
        isFiltered,
        showTable,
        isTableEmpty,
        emptyStateTitle,
        emptyStateDescription,
        footerLabel,
        kpiTickCount,
        showTableLoader,
        isAppendingPages,
        tableLoaderMinHeight: TABLE_SCROLL_Y,
        tableEmptyStateMinHeight: TABLE_EMPTY_STATE_MIN_HEIGHT,
        getRowClassName,
        handleRowClick,
        clearCellHover,
        isChartTableRefetching,
    };
}
